import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, FileSearch, Download, ArrowUpRight, Sparkles, Calendar, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API_BASE } from "@/lib/api";
import { fmtDate, daysUntil, relTime, fileSize } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { PageHeader, StatusChip, EmptyState, Skeleton, Card } from "@/components/common";
import { cn } from "@/lib/utils";

function CoverageBar({ stats }) {
  const total = stats?.total || 0;
  if (!total) return null;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" title={`${stats.covered} covered, ${stats.needs_review} need review, ${stats.not_covered} not covered`}>
      <div className="bg-[hsl(var(--status-ok))]" style={{ width: pct(stats.covered) }} />
      <div className="bg-[hsl(var(--status-warn))]" style={{ width: pct(stats.needs_review) }} />
      <div className="bg-[hsl(var(--status-bad))]" style={{ width: pct(stats.not_covered) }} />
    </div>
  );
}

export function UploadTenderDialog({ open, onOpenChange, initialFiles }) {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [buyer, setBuyer] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setFiles(initialFiles || []);
      setTitle("");
      setBuyer("");
    }
  }, [open, initialFiles]);

  const submit = async () => {
    if (!files.length) {
      toast.error("Add at least one document");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("title", title);
      fd.append("buyer", buyer);
      const r = await api.post("/tenders", fd);
      toast.success("Tender uploaded - analysis running", { description: "Requirements, deadlines and the compliance matrix appear in about a minute." });
      onOpenChange(false);
      navigate(`/tenders/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="tender-upload-dialog">
        <DialogHeader><DialogTitle>New tender</DialogTitle><DialogDescription>Upload the tender documents. The AI extracts every requirement, deadline and evaluation criterion and builds a compliance matrix against our catalog.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]); }}
            className="rounded-xl border-2 border-dashed p-5 text-center"
            data-testid="tender-upload-dropzone"
          >
            <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.xlsx,.txt" className="hidden" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="tender-upload-input" />
            <Upload className="mx-auto h-5 w-5 text-muted-foreground" />
            <div className="mt-2 text-sm font-medium">Drop PDF, Word or Excel files</div>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()} data-testid="tender-upload-choose-button">Choose files</Button>
          </div>
          {files.length > 0 && (
            <ul className="space-y-1" data-testid="tender-upload-file-list">
              {files.map((f, i) => <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded-lg border bg-white px-3 py-1.5 text-sm"><span className="inline-flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{f.name}</span><span className="text-xs text-muted-foreground">{fileSize(f.size)}</span></span><button type="button" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button></li>)}
            </ul>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="t-title">Title <span className="text-muted-foreground">(optional)</span></Label><Input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="AI fills this from the document" className="h-10 rounded-[10px]" data-testid="tender-upload-title-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="t-buyer">Buyer <span className="text-muted-foreground">(optional)</span></Label><Input id="t-buyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Contracting authority" className="h-10 rounded-[10px]" data-testid="tender-upload-buyer-input" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy || !files.length} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="tender-upload-submit-button"><Sparkles className="h-4 w-4" /> {busy ? "Uploading..." : "Upload and analyse"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function TendersPage() {
  const [tenders, setTenders] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [sampleFiles, setSampleFiles] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);

  const load = () => api.get("/tenders").then((r) => setTenders(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => {
    load();
    const t = setInterval(() => {
      setTenders((cur) => {
        if (cur && cur.some((x) => !["ready", "failed"].includes(x.status))) load();
        return cur;
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const trySample = async () => {
    setLoadingSample(true);
    try {
      const r = await fetch(`${API_BASE}/public/sample-tender.pdf`);
      const blob = await r.blob();
      const file = new File([blob], "Bergen_Kommune_Tender_2025-1148_sample.pdf", { type: "application/pdf" });
      setSampleFiles([file]);
      setOpen(true);
    } catch {
      toast.error("Could not fetch the sample tender");
    } finally {
      setLoadingSample(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Sales"
        title="Tenders"
        description="Upload a 200-page tender and get requirements, deadlines, compliance matrix and a first solution proposal - in minutes, not days."
        actions={<>
          <Button variant="outline" className="gap-2" onClick={trySample} disabled={loadingSample} data-testid="tenders-try-sample-button"><FileSearch className="h-4 w-4" /> {loadingSample ? "Fetching..." : "Try with sample tender"}</Button>
          <Button className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" onClick={() => { setSampleFiles(null); setOpen(true); }} data-testid="tenders-upload-button"><Upload className="h-4 w-4" /> Upload tender</Button>
        </>}
      />
      {error && <div className="mb-4 rounded-xl border border-[hsl(var(--status-bad)/0.3)] bg-[hsl(var(--status-bad)/0.06)] px-4 py-3 text-sm">{error}</div>}
      {!tenders ? (
        <div className="grid gap-4 md:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-44" />)}</div>
      ) : tenders.length === 0 ? (
        <EmptyState icon={FileSearch} title="No tenders yet" description="Upload a tender to get requirements, deadlines and a compliance matrix in minutes." action={<Button onClick={() => setOpen(true)} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]"><Upload className="h-4 w-4" /> Upload tender</Button>} secondary={<Button variant="outline" asChild><a href={`${API_BASE}/public/sample-tender.pdf`} className="gap-2"><Download className="h-4 w-4" /> Sample tender</a></Button>} testId="tenders-empty" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="tenders-list">
          {tenders.map((t) => {
            const dl = t.submission_deadline ? daysUntil(t.submission_deadline) : null;
            const working = !["ready", "failed"].includes(t.status);
            return (
              <Card key={t.id} className="flex flex-col p-5 transition-shadow hover:shadow-md-soft" data-testid="tenders-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/tenders/${t.id}`} className="line-clamp-2 text-base font-semibold leading-snug hover:underline underline-offset-4" data-testid="tenders-card-title">{t.title || "Untitled tender"}</Link>
                    <div className="mt-1 text-sm text-muted-foreground">{t.account_name || t.buyer || "Buyer not identified yet"}</div>
                  </div>
                  <StatusChip status={t.status} />
                </div>
                {working ? (
                  <div className="mt-4" data-testid="tenders-card-progress"><Progress value={t.progress || 10} className="h-1.5" /><div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" /> {t.step}</div></div>
                ) : t.status === "failed" ? (
                  <div className="mt-4 text-xs text-[hsl(var(--status-bad))]">{t.step}</div>
                ) : (
                  <div className="mt-4">
                    <div className="flex items-baseline justify-between text-xs text-muted-foreground"><span><span className="font-semibold tabular text-foreground">{t.stats?.total}</span> requirements · {t.stats?.must} must</span><span className="tabular">{t.stats?.covered} covered · <span className="text-[hsl(var(--status-warn))]">{t.stats?.needs_review} review</span> · <span className="text-[hsl(var(--status-bad))]">{t.stats?.not_covered} not</span></span></div>
                    <div className="mt-1.5"><CoverageBar stats={t.stats} /></div>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {t.submission_deadline ? <>Submission {fmtDate(t.submission_deadline)} {dl !== null && <span className={cn("tabular", dl <= 7 ? "font-medium text-[hsl(var(--status-warn))]" : "")}>({dl < 0 ? "passed" : `${dl} days`})</span>}</> : `Uploaded ${relTime(t.created_at)}`}</span>
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" asChild><Link to={`/tenders/${t.id}`} data-testid="tenders-card-open-link">Open <ArrowUpRight className="h-3.5 w-3.5" /></Link></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <UploadTenderDialog open={open} onOpenChange={setOpen} initialFiles={sampleFiles} />
    </div>
  );
}
